/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: APR.INBOX
 *
 * Approver inbox for the generic Approval engine. Lists requests where the
 * current user has a pending step at the request's current level, and records
 * decisions via the engine:
 *   - apr_decide              APPROVE / REJECT / ABSTAIN / RECUSE (quorum, level
 *                             advancement, staged-write commit on final approval)
 *   - apr_request_changes     send back to the requester (E5)
 *   - apr_decide_bulk         bulk approve/reject the selected requests (E4)
 *   - apr_reassign (admin)    move a pending step to another user (E1)
 *   - apr_override (admin)    Override-Password gated force-finalize (E4)
 */

/* rbac-affordance-ok: the authority here is BEING THE ASSIGNED APPROVER, not a
   page permission. The inbox selects approver_user_id = my id, and apr_decide /
   apr_decide_bulk / apr_request_changes re-check it server-side, returning
   APR.NOT_AN_APPROVER otherwise. Gating on can_change would silently strip
   approval rights from any approver whose role lacks it on this page.
   apr_override is separately protected by the §12 override password. */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { PersonalizationProvider, PersonalizationToggle } from '../../components/personalization';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';

type Decision = 'APPROVE' | 'REJECT' | 'ABSTAIN' | 'RECUSE';

interface StepRow { approval_step_id: number; approval_request_id: number; level: number; approver_source: string | null; }
interface RequestRow {
  approval_request_id: number; entity_name: string; record_id: string | null;
  state: string; current_level: number; active_levels: number[] | null; staged_operation: string | null;
  requested_by_user_id: number | null; requested_at: string;
}
interface InboxItem { step: StepRow; request: RequestRow; }

export interface MyApprovalsPageProps { session: AuthSession; }

export function MyApprovalsPage({ session }: MyApprovalsPageProps) {
  return (
    <PersonalizationProvider pageCode="APR.INBOX" session={session}>
      <MyApprovalsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function MyApprovalsPageInner({ session }: MyApprovalsPageProps) {
  const { t } = useTranslation();
  const myId = session.effective_user.user_id;
  // UI hint only — the engine enforces admin server-side (csm_current_is_admin) on
  // apr_override / apr_reassign, so this just decides whether to surface the controls.
  const isAdmin = session.effective_user.roles.some(r => /ADMIN/i.test(r.internal_code));
  const [items, setItems] = useState<InboxItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [comment, setComment] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the inbox).
  const [facets, setFacets] = useState<{ entity: string[]; level: string[] }>({ entity: [], level: [] });
  const [requesterNames, setRequesterNames] = useState<Record<number, string>>({});

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data: steps, error: e1 } = await apiDb.from('apr_step')
        .select('approval_step_id, approval_request_id, level, approver_source, decision')
        .eq('approver_user_id', myId).is('decision', null);
      if (e1) throw e1;
      const stepRows = (steps ?? []) as (StepRow & { decision: string | null })[];
      const reqIds = [...new Set(stepRows.map(s => s.approval_request_id))];
      if (reqIds.length === 0) { setItems([]); setSelected(new Set()); return; }

      const { data: reqs, error: e2 } = await apiDb.from('apr_request')
        .select('approval_request_id, entity_name, record_id, state, current_level, active_levels, staged_operation, requested_by_user_id, requested_at')
        .in('approval_request_id', reqIds).eq('state', 'PENDING');
      if (e2) throw e2;
      const reqById = new Map<number, RequestRow>(((reqs ?? []) as RequestRow[]).map(r => [r.approval_request_id, r] as const));

      // A step is actionable when its level is in the request's active set (parallel
      // stages activate several levels at once; falls back to [current_level]).
      const list: InboxItem[] = [];
      for (const s of stepRows) {
        const r = reqById.get(s.approval_request_id);
        const active = r?.active_levels ?? (r ? [r.current_level] : []);
        if (r && active.includes(s.level)) list.push({ step: s, request: r });
      }
      setItems(list);
      const requesterIds = Array.from(new Set(list.map(it => it.request.requested_by_user_id).filter((x): x is number => x != null)));
      if (requesterIds.length) {
        const { data: us } = await apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', requesterIds);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) m[u.user_id] = u.display_name || u.email || '—';
        setRequesterNames(m);
      } else { setRequesterNames({}); }
      setSelected(prev => new Set([...prev].filter(id => list.some(it => it.request.approval_request_id === id))));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  // reload is re-created each render and the effect must run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  const run = async (label: string, fn: () => Promise<{ ok: boolean; error_code?: string; state?: string } | null>) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await fn();
      if (res && res.ok === false) throw new Error(res.error_code ?? 'APR.FAILED');
      setNotice(res?.state ? t('apr_inbox.notice_with_state', '{{label}} (now {{state}}).', { label, state: res.state }) : t('apr_inbox.notice', '{{label}}.', { label }));
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const decide = (requestId: number, decision: Decision) =>
    run(t('apr_inbox.label_decide', 'Request {{id}}: {{decision}}', { id: requestId, decision: decision.toLowerCase() }), async () => {
      const { data, error: e } = await apiDb.rpc('apr_decide', { p_request_id: requestId, p_decision: decision, p_comment: comment[requestId] ?? null });
      if (e) throw e; return data as { ok: boolean; error_code?: string; state?: string };
    });

  const requestChanges = (requestId: number) =>
    run(t('apr_inbox.label_request_changes', 'Request {{id}}: changes requested', { id: requestId }), async () => {
      const { data, error: e } = await apiDb.rpc('apr_request_changes', { p_request_id: requestId, p_comment: comment[requestId] ?? null });
      if (e) throw e; return data as { ok: boolean; error_code?: string; state?: string };
    });

  const bulkDecide = (decision: 'APPROVE' | 'REJECT') =>
    run(t('apr_inbox.label_bulk', 'Bulk {{decision}} ({{count}})', { decision: decision.toLowerCase(), count: selected.size }), async () => {
      const p_items = [...selected].map(id => ({ request_id: id, decision, comment: comment[id] ?? null }));
      const { error: e } = await apiDb.rpc('apr_decide_bulk', { p_items });
      if (e) throw e; return { ok: true };
    });

  const reassign = (step: StepRow) => {
    const to = window.prompt(t('apr_inbox.prompt_reassign', 'Reassign this step to user id:'));
    if (!to) return;
    void run(t('apr_inbox.label_reassign', 'Step {{id}} reassigned', { id: step.approval_step_id }), async () => {
      const { data, error: e } = await apiDb.rpc('apr_reassign', { p_step_id: step.approval_step_id, p_to_user: Number(to), p_reason: 'inbox reassign' });
      if (e) throw e; return data as { ok: boolean; error_code?: string };
    });
  };

  const override = (requestId: number, decision: 'APPROVE' | 'REJECT') => {
    const pw = window.prompt(t('apr_inbox.prompt_override_password', 'Override password to force {{decision}}:', { decision }));
    if (!pw) return;
    const reason = window.prompt(t('apr_inbox.prompt_reason', 'Reason (required):')) ?? '';
    void run(t('apr_inbox.label_override', 'Request {{id}} overridden', { id: requestId }), async () => {
      const { data, error: e } = await apiDb.rpc('apr_override', { p_request_id: requestId, p_decision: decision, p_password: pw, p_reason: reason });
      if (e) throw e; return data as { ok: boolean; error_code?: string; state?: string };
    });
  };

  const toggle = (id: number) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const btn = 'rounded border px-2.5 py-1 text-csm-sm disabled:opacity-60';

  // §17.B5: distinct facet options derived from the loaded inbox.
  const entityFacetOptions = useMemo(() => {
    const uniq = [...new Set(items.map(it => it.request.entity_name).filter(Boolean))].sort();
    return uniq.map(v => ({ value: v, label: v }));
  }, [items]);
  const levelFacetOptions = useMemo(() => {
    const uniq = [...new Set(items.map(it => String(it.request.current_level)))].sort((a, b) => Number(a) - Number(b));
    return uniq.map(v => ({ value: v, label: t('apr_inbox.level', 'Level {{level}}', { level: v }) }));
  }, [items, t]);
  const anyFilterActive = filter.trim().length > 0 || facets.entity.length + facets.level.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity: [], level: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    return items.filter(({ request }) => {
      const matchText = !q ||
        request.entity_name.toLowerCase().includes(q) || (request.record_id ?? '').toLowerCase().includes(q);
      const matchEntity = facets.entity.length === 0 || facets.entity.includes(request.entity_name);
      const matchLevel  = facets.level.length === 0  || facets.level.includes(String(request.current_level));
      return matchText && matchEntity && matchLevel;
    });
  }, [items, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('apr_inbox.heading', 'My Approvals')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('apr_inbox.filter_placeholder', 'Filter requests…')}
          className="h-7 w-56 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('apr_inbox.facet_entity', 'Entity')} options={entityFacetOptions} selected={facets.entity} onChange={v => setFacets(f => ({ ...f, entity: v }))} />
        <FacetFilter label={t('apr_inbox.facet_level', 'Level')} options={levelFacetOptions} selected={facets.level} onChange={v => setFacets(f => ({ ...f, level: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('apr_inbox.refresh', 'Refresh')}</button>
        {selected.size > 0 && (
          <span className="ml-2 flex items-center gap-2">
            <span className="text-csm-xs text-csm-ink-soft">{t('apr_inbox.selected_count', '{{count}} selected', { count: selected.size })}</span>
            <button onClick={() => void bulkDecide('APPROVE')} disabled={busy} className={btn + ' border-primary bg-primary text-white'}>{t('apr_inbox.approve_selected', 'Approve selected')}</button>
            <button onClick={() => void bulkDecide('REJECT')} disabled={busy} className={btn + ' border-csm-danger-border bg-csm-danger-soft text-csm-danger'}>{t('apr_inbox.reject_selected', 'Reject selected')}</button>
          </span>
        )}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('apr_inbox.working', 'Working…')}</span>}
        {notice && <span className="text-csm-xs text-primary">{notice}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <ErrorBanner
          message={t('apr_inbox.error_banner', "Couldn't complete the last approvals operation.")}
          detail={error}
          retry_label={t('common.retry', 'Retry')}
          on_retry={() => void reload()}
          details_label={t('common.details', 'Details')}
        />
      )}

      <div className="flex-1 overflow-auto p-3">
        {filtered.length === 0 && !busy && (
          <div className="px-2 py-8 text-center">
            <EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('apr_inbox.empty', 'Nothing awaiting your approval.')} />
          </div>
        )}
        <div className="flex flex-col gap-2">
          {filtered.map(({ step, request }) => (
            <div key={step.approval_step_id} className="rounded border border-csm-border bg-white p-3">
              <div className="mb-1 flex items-center gap-2 text-csm-sm">
                <input type="checkbox" checked={selected.has(request.approval_request_id)} onChange={() => toggle(request.approval_request_id)} className="h-4 w-4" />
                <span className="font-mono font-semibold">{request.entity_name}</span>
                <span className="text-csm-ink-soft">#{request.record_id ?? `(staged ${request.staged_operation})`}</span>
                <span className="ml-2 rounded bg-csm-surface-alt px-1.5 py-0.5 text-csm-2xs uppercase tracking-wide text-csm-ink-soft">{t('apr_inbox.level', 'Level {{level}}', { level: request.current_level })}</span>
                <span className="text-csm-2xs text-csm-watermark">{t('apr_inbox.via', 'via {{source}}', { source: step.approver_source })}</span>
                <span className="ml-auto text-csm-2xs text-csm-watermark">{t('apr_inbox.requested_by', 'requested {{when}} by {{who}}', { when: new Date(request.requested_at).toLocaleString(), who: request.requested_by_user_id == null ? '—' : (requesterNames[request.requested_by_user_id] ?? '—') })}</span>
              </div>
              <textarea
                rows={2} value={comment[request.approval_request_id] ?? ''}
                onChange={e => setComment(c => ({ ...c, [request.approval_request_id]: e.target.value }))}
                placeholder={t('apr_inbox.comment_placeholder', 'Optional comment…')}
                className="mb-2 w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none"
              />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => decide(request.approval_request_id, 'APPROVE')} disabled={busy} className={btn + ' border-primary bg-primary text-white hover:bg-primary-hover'}>{t('apr_inbox.approve', 'Approve')}</button>
                <button onClick={() => decide(request.approval_request_id, 'REJECT')} disabled={busy} className={btn + ' border-csm-danger-border bg-csm-danger-soft text-csm-danger'}>{t('apr_inbox.reject', 'Reject')}</button>
                <button onClick={() => requestChanges(request.approval_request_id)} disabled={busy} className={btn + ' border-csm-warning-border bg-csm-warning-soft text-csm-warning'}>{t('apr_inbox.request_changes', 'Request changes')}</button>
                <button onClick={() => decide(request.approval_request_id, 'ABSTAIN')} disabled={busy} className={btn + ' border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}>{t('apr_inbox.abstain', 'Abstain')}</button>
                <button onClick={() => decide(request.approval_request_id, 'RECUSE')} disabled={busy} className={btn + ' border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}>{t('apr_inbox.recuse', 'Recuse')}</button>
                {isAdmin && (
                  <span className="ml-auto flex gap-2">
                    <button onClick={() => reassign(step)} disabled={busy} className={btn + ' border-csm-border bg-white text-csm-ink-soft'}>{t('apr_inbox.reassign', 'Reassign')}</button>
                    <button onClick={() => override(request.approval_request_id, 'APPROVE')} disabled={busy} className={btn + ' border-csm-border bg-white text-csm-ink-soft'}>{t('apr_inbox.override', 'Override…')}</button>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
